import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ApiEndpoints from '../../const/ApiEndpoints';
import { SwalToast } from '../../components/Common';

/**
 * Custom hook for managing color palettes and preferences.
 *
 * @param {Object} initialState - Initial state for the hook.
 * @param {string} initialState.column - The selected column.
 * @param {Object} initialState.color - The color preferences.
 * @param {Object} initialState.hexCodes - Hex color codes.
 * @returns {{
 *   hexCodes: Object.<string, string>,
 *   sethexCodes: React.Dispatch<React.SetStateAction<Object.<string, string>>>,
 *   selectColumn: string,
 *   setselectedColumn: React.Dispatch<React.SetStateAction<string>>,
 *   selectColumnHelper: (column: string) => void,
 *   color: { background: string },
 *   setcolor: React.Dispatch<React.SetStateAction<{ background: string }>>,
 *   graphSetting: (body: Object) => Promise<any>,
 *   fetchGraphColor: (module: string) => Promise<any>
 * }} Object containing state variables and functions of color palettes.
 * @throws {Error} Throws an error if color preferences cannot be fetched.
 */
export default function useColorPallete() {
  const [hexCodes, sethexCodes] = React.useState({});
  const [selectColumn, setselectedColumn] = React.useState('');
  const [color, setcolor] = React.useState({ background: 'fff' });
  const defaultLocalStorageTheme = JSON.parse(
    localStorage.getItem('theme') || '{}'
  );
  const [currentSavedTheme, setCurrentSavedTheme] = useState(
    defaultLocalStorageTheme || {}
  );
  const resetGraphSetting = (module) => {
    axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.DI}${ApiEndpoints.PROJECT_MANAGEMENT.GRAPH_COLOR_CHANGE}`,
        { module }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Colors set to default successfully.',
          });
          return res;
        },
        (err) => {
          throw err;
        }
      );
  };

  /**
   * Updates the color preference for graphs.
   * @function
   * @param {Object} body - The request body containing color preferences.
   * @returns {Promise<any>} A promise that resolves when the color preference is updated.
   */
  const graphSetting = (body) =>
    axios
      .put(
        ApiEndpoints.MODULE_BASE_URL.DI +
          ApiEndpoints.PROJECT_MANAGEMENT.GRAPH_COLOR_CHANGE,
        body
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'Color preference changed successfully.',
          });
          return res;
        },
        (err) => {
          throw err;
        }
      );
  /**
   * Fetches the graph color preference for a specific module.
   * @function
   * @param {string} module - The module name.
   * @returns {Promise<any>} A promise that resolves with the fetched color preference.
   */
  const fetchGraphColor = (module) =>
    axios
      .get(
        `${ApiEndpoints.MODULE_BASE_URL.DI}${ApiEndpoints.PROJECT_MANAGEMENT.GRAPH_COLOR_CHANGE}?module=${module}`
      )
      .then(
        (res) => res,
        (err) => {
          throw err;
        }
      );
  useEffect(() => {
    if (selectColumn) {
      fetchGraphColor(selectColumn).then(
        (res) => {
          sethexCodes({ ...res.data });
        },
        () => {
          SwalToast({
            icon: 'error',
            title: 'Failed to fetch color preferences.',
          });
        }
      );
    }
  }, [selectColumn]);

  /**
   * Updates the selected column and fetches corresponding color preferences.
   * @function
   * @param {string} column - The column to select.
   */
  const selectColumnHelper = (column) => {
    setselectedColumn(column);
    if (!column) {
      return;
    }
    fetchGraphColor(column).then(
      (res) => {
        sethexCodes({ ...res.data });
      },
      () => {
        SwalToast({
          icon: 'error',
          title: 'Failed to fetch color preferences.',
        });
      }
    );
  };
  const saveThemeInLocalStorage = (theme) => {
    localStorage.setItem('theme', JSON.stringify(theme));
  };

  const saveTheme = (body) => {
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.DI +
          ApiEndpoints.PROJECT_MANAGEMENT.SAVE_THEME
        }`,
        body,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      .then((res) => {
        saveThemeInLocalStorage(body);
        return res;
      })
      .catch((err) => {
        return err;
      });
  };

  const getCurrentTheme = () => {
    axios
      .get(
        `${
          ApiEndpoints.MODULE_BASE_URL.DI +
          ApiEndpoints.PROJECT_MANAGEMENT.GET_CURRENT_THEME
        }`,
        {
          headers: {
            'Content-Type': 'application/json',
            org: localStorage.getItem('REALM_ID'),
          },
        }
      )
      .then(
        (res) => {
          if (res?.data) {
            setCurrentSavedTheme(res.data);
            saveThemeInLocalStorage(res.data);
          } else {
            setCurrentSavedTheme({});
          }
          return res;
        },
        (error) => {
          setCurrentSavedTheme({});
          return error;
          // return error;
        }
      );
  };
  return {
    hexCodes,
    sethexCodes,
    selectColumn,
    setselectedColumn,
    selectColumnHelper,
    color,
    resetGraphSetting,
    setcolor,
    graphSetting,
    fetchGraphColor,
    saveTheme,
    getCurrentTheme,
    currentSavedTheme,
  };
}
